/**
 * Reprices a product card against the quantity in its own stepper.
 *
 * A card offers "Buy more & Save up to 16%" and a quantity stepper, and then held its price
 * at the single-unit figure however many you asked for — the card promised a saving and
 * then declined to show it. The product page panel has done this since it was built; this
 * is the same behaviour on the card.
 *
 * Two states, which is what the client's comment asks for in one sentence: the span of the
 * ladder "before selecting", and the price for the quantity chosen "after selecting".
 *
 *   quantity 1  — lowest to highest, as the product page panel shows it. Most shoppers
 *                 never touch a stepper on a card, so at rest the card has to say that
 *                 volume pricing exists without being asked.
 *   quantity 2+ — the unit price at that quantity. Unit, not line total: it is the number
 *                 that carries the saving, and it is what every other card on the row is
 *                 showing, so comparing down a grid stays honest.
 *
 * Tiers come from the savings badge's data attribute. That badge is on every card that has
 * a ladder, and only when the discount app is on, so its presence is already the exact
 * condition under which a card should be doing any of this.
 */
const CARD = 'product-card';

// Bumped on every change to this behaviour. The asset is public, so fetching it says
// whether a commit has actually reached the theme — the CDN copy is minified and greping
// the source for a colour or a selector can lie about that.
const BUILD = 'card-volume-price/7';

/** @type {WeakMap<Element, {qty: number, price: number}[]>} */
const laddersByCard = new WeakMap();

/**
 * @param {Element} card
 * @returns {{qty: number, price: number}[]}
 */
const ladderFor = (card) => {
  const cached = laddersByCard.get(card);

  if (cached) return cached;

  const badge = card.querySelector('.civital-savings-badge[data-civital-tiers]');
  let tiers = [];

  /*
   * Say so, once, when a card advertises a saving it cannot price. Silence here is what
   * made this take a day to diagnose: the badge rendered, the script ran, and nothing
   * anywhere said the two were not talking to each other. The cause turned out to be a
   * file that never reached the theme, which no amount of reasoning about the code could
   * have found — only the page saying something was wrong.
   */
  if (!badge) {
    const anyBadge = card.querySelector('.civital-savings-badge');

    if (anyBadge instanceof HTMLElement && !laddersByCard.has(card)) {
      console.warn(`[${BUILD}] Card advertises a saving but carries no ladder.`, anyBadge);
    }
  }

  if (badge instanceof HTMLElement) {
    try {
      tiers = JSON.parse(badge.dataset.civitalTiers || '[]')
        .map((tier) => ({ qty: Number(tier.qty), price: Number(tier.price) }))
        .filter((tier) => Number.isFinite(tier.qty) && Number.isFinite(tier.price) && tier.qty > 0)
        .sort((a, b) => a.qty - b.qty);
    } catch (_) {
      tiers = [];
    }
  }

  laddersByCard.set(card, tiers);

  return tiers;
};

/**
 * @param {Element} card
 * @param {number} amount
 * @returns {string}
 */
const money = (card, amount) => {
  const badge = card.querySelector('.civital-savings-badge[data-civital-currency]');
  const currency = (badge instanceof HTMLElement && badge.dataset.civitalCurrency) || 'GBP';

  return new Intl.NumberFormat(document.documentElement.lang || 'en-GB', {
    style: 'currency',
    currency,
  }).format(amount);
};

/** @param {Element} card */
const reprice = (card) => {
  const tiers = ladderFor(card);

  if (tiers.length === 0) return;

  const input = card.querySelector('.quantity-selector input[name="quantity"]');
  const price = card.querySelector('.price');

  if (!(input instanceof HTMLInputElement) || !(price instanceof HTMLElement)) return;

  // Keep what the server rendered: it is what a quantity below the first tier costs, and
  // it is already in the shop's own money format.
  if (price.dataset.civitalBasePrice == null) {
    price.dataset.civitalBasePrice = price.textContent ?? '';
  }

  const qty = Math.max(1, Number(input.value) || 1);

  if (qty <= 1) {
    price.textContent = rangeFor(card) ?? price.dataset.civitalBasePrice;
    return;
  }

  let match = null;

  for (const tier of tiers) {
    if (qty >= tier.qty) match = tier;
  }

  price.textContent = match ? money(card, match.price) : price.dataset.civitalBasePrice;
};

/**
 * The resting state: the two ends of the ladder, formatted by Liquid.
 *
 * Liquid knows both prices at render time and `money` is the shop's own format, which Intl
 * only approximates — so the finished string comes down on the element rather than a
 * template this has to fill in. Absent when every tier is the same price, which is not a
 * range.
 *
 * @param {Element} card
 * @returns {string | null}
 */
const rangeFor = (card) => {
  const badge = card.querySelector('.civital-savings-badge[data-civital-range]');

  return badge instanceof HTMLElement ? badge.dataset.civitalRange ?? null : null;
};

/** @param {Event} event */
const onCardEvent = (event) => {
  const target = event.target;

  if (!(target instanceof Element)) return;

  const card = target.closest(CARD);

  if (card) reprice(card);
};

document.addEventListener('input', onCardEvent);
document.addEventListener('change', onCardEvent);

/*
 * The plus and minus buttons set input.value in script, which fires no input event, so the
 * click is what has to be listened for — one frame later, once the component has written
 * the new value.
 */
document.addEventListener('click', (event) => {
  const target = event.target;

  if (!(target instanceof Element)) return;
  if (!target.closest('.quantity-selector button')) return;

  const card = target.closest(CARD);

  if (card) requestAnimationFrame(() => reprice(card));
});

/*
 * Cards that have already been priced once.
 *
 * Filtering and sorting swap the whole grid for freshly rendered markup, and infinite
 * scroll appends more, so priming once at load is not enough — a filtered page went back
 * to showing the single-unit price. Watching the document catches every route that
 * replaces cards without needing to know which one did it.
 *
 * The set is what stops that watch from feeding itself: repricing writes to the price
 * element, which is a mutation inside the observed tree. Cards already seen are skipped, so
 * the write triggers a callback that does nothing and stops there. A WeakSet also means
 * replaced cards are collected rather than leaked.
 */
const primed = new WeakSet();

const primeNewCards = () => {
  for (const card of document.querySelectorAll(CARD)) {
    if (primed.has(card)) continue;

    primed.add(card);
    reprice(card);
  }
};

primeNewCards();
document.addEventListener('DOMContentLoaded', primeNewCards);

if ('MutationObserver' in window) {
  let scheduled = false;

  const observer = new MutationObserver(() => {
    if (scheduled) return;

    scheduled = true;
    requestAnimationFrame(() => {
      scheduled = false;
      primeNewCards();
    });
  });

  observer.observe(document.body, { childList: true, subtree: true });
}
